"use client";

import {
  nextColumnSortDirection,
  type ColumnSortDirection,
} from "@/lib/utils/column-order";
import { cn } from "@/lib/utils/general-utils";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

export type { ColumnSortDirection };

export function ColumnSortIndicator({
  sortDirection,
}: {
  readonly sortDirection: ColumnSortDirection;
}) {
  if (sortDirection === "asc") {
    return <ArrowUp className="size-3" aria-hidden="true" />;
  }
  if (sortDirection === "desc") {
    return <ArrowDown className="size-3" aria-hidden="true" />;
  }
  return <ArrowUpDown className="size-3 opacity-30" aria-hidden="true" />;
}

function cycleSortLabel(
  sortDirection: ColumnSortDirection,
  columnLabel?: string,
): string {
  const next = nextColumnSortDirection(sortDirection);
  if (next === "asc") {
    return columnLabel ? `Sort ${columnLabel} ascending` : "Sort ascending";
  }
  if (next === "desc") {
    return columnLabel ? `Sort ${columnLabel} descending` : "Sort descending";
  }
  return columnLabel ? `Clear ${columnLabel} sort` : "Clear sort";
}

export interface ColumnSortControlsProps {
  readonly sortDirection: ColumnSortDirection;
  readonly onSortAsc: () => void;
  readonly onSortDesc: () => void;
  readonly onClearSort: () => void;
  /** When set, button accessible names are unique per column */
  readonly columnLabel?: string;
  /** Prefix "Sort" legend — used in filter dropdowns, omitted in dense rows */
  readonly showLegend?: boolean;
}

export function ColumnSortControls({
  sortDirection,
  onSortAsc,
  onSortDesc,
  onClearSort,
  columnLabel,
  showLegend = true,
}: ColumnSortControlsProps) {
  const label = cycleSortLabel(sortDirection, columnLabel);

  const handleCycle = () => {
    const next = nextColumnSortDirection(sortDirection);
    if (next === "asc") {
      onSortAsc();
      return;
    }
    if (next === "desc") {
      onSortDesc();
      return;
    }
    onClearSort();
  };

  return (
    <div
      className="flex items-center gap-1"
      role="group"
      aria-label={columnLabel ? `${columnLabel} sort` : "Column sort"}
    >
      {showLegend ? (
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
          Sort
        </span>
      ) : null}
      <button
        type="button"
        aria-label={label}
        title={label}
        aria-pressed={sortDirection !== false}
        onClick={handleCycle}
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded p-0.5 transition-colors",
          "hover:bg-gray-50 hover:text-gray-700",
          sortDirection ? "text-gray-700" : "text-text-tertiary",
        )}
      >
        <ColumnSortIndicator sortDirection={sortDirection} />
      </button>
    </div>
  );
}
